import { useState, useEffect } from 'react';
import { Cookie, X, Shield, Settings } from 'lucide-react';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
      setHasConsent(false);
    } else {
      setHasConsent(true);
      setPreferences(JSON.parse(consent));
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    setHasConsent(true);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
    setHasConsent(true);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
    setHasConsent(true);
  };

  const openSettings = () => {
    setShowBanner(true);
    setShowSettings(true);
  };

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      {hasConsent && !showBanner && (
        <button
          onClick={openSettings}
          className="fixed left-6 bottom-6 z-40 bg-white hover:bg-slate-50 p-4 rounded-full shadow-lg hover:shadow-xl transition-all border-2 border-slate-200 hover:border-slate-300 group"
          aria-label="Cookie-Einstellungen"
          title="Cookie-Einstellungen anpassen"
        >
          <Cookie className="w-6 h-6 text-slate-700 group-hover:text-slate-900 transition" />
        </button>
      )}

      {showBanner && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
        {!showSettings ? (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-slate-100 p-3 rounded-xl">
                  <Cookie className="w-6 h-6 text-slate-700" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Cookie-Einstellungen</h3>
                  <p className="text-sm text-slate-600 mt-1">Ihre Privatsphäre ist uns wichtig</p>
                </div>
              </div>
              <button
                onClick={handleAcceptNecessary}
                className="text-slate-400 hover:text-slate-600 transition"
                aria-label="Schliessen"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-slate-700 leading-relaxed mb-6">
              Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
              Einige Cookies sind notwendig für den Betrieb der Website, während andere uns helfen,
              die Website zu verbessern und Ihnen relevante Inhalte anzuzeigen.
            </p>

            <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-slate-900 font-semibold mb-1">Ihre Daten sind sicher</p>
                  <p className="text-slate-600">
                    Wir geben Ihre Daten nicht an Dritte weiter und halten uns strikt an die Schweizer Datenschutzgesetze.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-slate-800 hover:bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md hover:shadow-lg"
              >
                Alle akzeptieren
              </button>
              <button
                onClick={handleAcceptNecessary}
                className="flex-1 bg-white hover:bg-slate-50 text-slate-800 px-6 py-3 rounded-lg font-semibold transition border-2 border-slate-200 hover:border-slate-300"
              >
                Nur notwendige
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center space-x-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-3 rounded-lg font-semibold transition"
              >
                <Settings className="w-5 h-5" />
                <span>Anpassen</span>
              </button>
            </div>

            <div className="mt-4 text-center">
              <a href="/datenschutz" className="text-sm text-slate-600 hover:text-slate-900 underline">
                Mehr in unserer Datenschutzerklärung
              </a>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Cookie-Präferenzen</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-bold text-slate-900">Notwendige Cookies</h4>
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-semibold">
                      Erforderlich
                    </span>
                  </div>
                  <div className="bg-green-500 w-12 h-6 rounded-full flex items-center px-1">
                    <div className="bg-white w-5 h-5 rounded-full shadow-sm ml-auto"></div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Diese Cookies sind für den Betrieb der Website unerlässlich und können nicht deaktiviert werden.
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border-2 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-slate-900">Analyse-Cookies</h4>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`w-12 h-6 rounded-full transition ${
                      preferences.analytics ? 'bg-green-500' : 'bg-slate-300'
                    } flex items-center px-1`}
                  >
                    <div
                      className={`bg-white w-5 h-5 rounded-full shadow-sm transition ${
                        preferences.analytics ? 'ml-auto' : ''
                      }`}
                    ></div>
                  </button>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, um die Nutzererfahrung zu verbessern.
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border-2 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-slate-900">Marketing-Cookies</h4>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`w-12 h-6 rounded-full transition ${
                      preferences.marketing ? 'bg-green-500' : 'bg-slate-300'
                    } flex items-center px-1`}
                  >
                    <div
                      className={`bg-white w-5 h-5 rounded-full shadow-sm transition ${
                        preferences.marketing ? 'ml-auto' : ''
                      }`}
                    ></div>
                  </button>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Diese Cookies werden verwendet, um Ihnen relevante Werbung und Angebote anzuzeigen.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-slate-800 hover:bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md hover:shadow-lg"
              >
                Einstellungen speichern
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 bg-white hover:bg-slate-50 text-slate-800 px-6 py-3 rounded-lg font-semibold transition border-2 border-slate-200 hover:border-slate-300"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
      )}
    </>
  );
}
